package xim.poc.game.configuration.v0.tactics.behaviors

import xim.math.Vector3f
import xim.poc.ActionTargetFilter
import xim.poc.ActorManager
import xim.poc.game.*
import xim.poc.game.GameEngine.displayName
import xim.poc.game.actor.components.resetRecastStates
import xim.poc.game.configuration.*
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.CombatBonusAggregator
import xim.poc.game.configuration.v0.GameV0
import xim.poc.game.configuration.v0.V0AbilityCost
import xim.poc.game.configuration.v0.behaviors.FixedWarpDestination
import xim.poc.game.configuration.v0.behaviors.V0MonsterController
import xim.poc.game.configuration.v0.behaviors.WarpStateMachine
import xim.poc.game.configuration.v0.tactics.*
import xim.poc.game.event.*
import xim.poc.gl.ByteColor
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.DamageTextManager
import xim.resource.*
import xim.resource.table.AbilityInfoTable.toAbilityInfo
import xim.resource.table.SpellInfoTable.toSpellInfo
import xim.util.addInPlace
import kotlin.math.min
import kotlin.math.roundToInt
import kotlin.random.Random
import kotlin.time.Duration
import kotlin.time.Duration.Companion.INFINITE
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

object TacticsBehaviorId: BehaviorId
data class TacticsChallengeBehaviorId(val challengeId: Int, val internalId: Int): BehaviorId

class TacticsSkillChoice(val skill: SkillId, val position: PanelPosition, val score: Float)

data class TacticsSkillQuery(
    val skill: SkillId,
    val target: ActorState,
    val originalPosition: PanelPosition,
    val destinationPosition: PanelPosition,
    val result: ApplierSuccessResult,
)

private class ChargingState(val routine: EffectRoutineInstance?)

open class TacticsBehaviorController(actorState: ActorState): V0MonsterController(actorState) {

    private val memoizedSkills = ArrayList<SkillId>()
    private var tpDamageResistanceStep = 0

    private val skillCounter = HashMap<SkillId, Int>()
    private var charging: ChargingState? = null

    private var warpStateMachine: WarpStateMachine<TurnLock>? = null

    final override fun onInitialized(): List<Event> {
        dynamicStatusResistance = StatusResistanceTracker().also {
            it[StatusEffect.Bind] = OccurrenceLimitStrategy(resetInterval = INFINITE, maxOccurrencesInInterval = 3)
            it[StatusEffect.Weight] = OccurrenceLimitStrategy(resetInterval = INFINITE, maxOccurrencesInInterval = 3)
        }

        recomputeSkills()
        return onBehaviorInitialized()
    }

    open fun onBehaviorInitialized(): List<Event> {
        return emptyList()
    }

    final override fun update(elapsedFrames: Float): List<Event> {
        updateWarpState(elapsedFrames)

        val position = actorState.getCursorPosition()
        actorState.targetable = position is PanelPosition

        actorState.setEngagedState(getEngagedState())

        applyBlur()

        return tacticsUpdate(elapsedFrames)
    }

    open fun getEngagedState(): EngagedState.State {
        return EngagedState.State.Engaged
    }

    open fun tacticsUpdate(elapsedFrames: Float): List<Event> {
        return emptyList()
    }

    final override fun getWeapon(): Pair<Int, Int> {
        return actorState.getUnit().getWeaponPower() to 300
    }

    override fun getSkills(): List<SkillId> {
        return memoizedSkills
    }

    final override fun getCombatStats(): CombatStats {
        val baseStats = actorState.getUnit().getCombatStats()
        val unitConfig = actorState.getUnitConfig() ?: return baseStats
        return baseStats.multiply(CombatStat.maxHp, unitConfig.hpMultiplier) * unitConfig.allStatMultiplier
    }

    final override fun getSkillCastTimeOverride(skill: SkillId?): Duration {
        return Duration.ZERO
    }

    final override fun applyMonsterBehaviorBonuses(aggregate: CombatBonusAggregate) {
        aggregate.movementSpeed += 50

        val unit = actorState.getUnit()
        TacticsInstincts.apply(unit.instincts, aggregate)

        val jobConfig = TacticsJobConfigs[unit.currentJob]
        val jobResistances = jobResistanceTables[jobConfig.element] ?: emptyMap()
        val resistMultiplier = getMatch().config.getJobResistMultiplier()

        if (!actorState.isEnemy()) {
            aggregate.criticalHitDamage += (20 * unit.getCurrentJobConfig().stats.dex.multiplier).roundToInt() - 20
        }

        jobResistances.forEach {
            aggregate.elementalResistance.addInPlace(it.key, (it.value * resistMultiplier).roundToInt())
        }

        applyTacticsBehaviorBonuses(aggregate)
    }

    open fun applyTacticsBehaviorBonuses(aggregate: CombatBonusAggregate) { }

    final override fun getSkillCostOverride(skill: SkillId): V0AbilityCost {
        val bonuses = CombatBonusAggregator[actorState]
        val baseCost = TacticsSkills[skill].cost

        if (skill is SpellSkillId && actorState.hasStatusEffect(StatusEffect.Spontaneity)) {
            return V0AbilityCost(baseCost = AbilityCost(type = AbilityCostType.Tp, value = 0))
        }

        val wisdom = actorState.getStatusEffect(StatusEffect.UnbridledWisdom)
        val wisdomBonus = if (wisdom != null && skill is SpellSkillId && skill.toSpellInfo().magicType == MagicType.BlueMagic) {
            wisdom.potency.toPenaltyMultiplier()
        } else {
            1f
        }

        val hasteMultiplier = bonuses.haste.toPenaltyMultiplier()
        val adjustedCost = (baseCost.baseCost.value * hasteMultiplier * wisdomBonus).roundToInt()

        return V0AbilityCost(baseCost = AbilityCost(type = AbilityCostType.Tp, value = adjustedCost))
    }

    override fun getSkillApplierOverride(skillId: SkillId): SkillApplier {
        return TacticsSkills[skillId].skillApplier
    }

    override fun getSkillAnimationOverride(skillId: SkillId): SkillId? {
        if (actorState.getJobConfig().learnsSkill(skillId)) { return null }
        return TacticsSkills.getNullable(skillId)?.crossJob?.link
    }

    override fun getSkillRangeOverride(skill: SkillId): SkillRangeInfo {
        return TacticsSkills[skill].rangeInfo
    }

    final override fun ignoresSkillRangeChecks(skill: SkillId): Boolean {
        return true
    }

    final override fun getSkillEffectedTargetType(skillId: SkillId): Int? {
        return TacticsSkills[skillId].aoeTargetFlag
    }

    final override fun onSkillInterrupted(skill: SkillId): List<Event> {
        actorState.consumeTp(300)
        getMatch().getCurrentTurn()?.onSkillExecuted()
        return emptyList()
    }

    final override fun onSkillExecuted(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        return listOf(Event { internalSkillExecutedEvent(primaryTargetContext) })
    }

    private fun internalSkillExecutedEvent(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        val match = getMatch()
        val currentTurn = match.getCurrentTurn() ?: throw IllegalStateException("No active turn?")

        maybeGainSpontaneity(primaryTargetContext)

        skillCounter.addInPlace(primaryTargetContext.skill, 1)
        currentTurn.onSkillExecuted()

        val lock = currentTurn.lock()
        AttackContext.compose(primaryTargetContext.context) {
            if (!maybeHandleTractor(primaryTargetContext, lock)) {
                lock.locked = false
                currentTurn.onSkillAnimationComplete(endWait = 500.milliseconds)
            }
        }

        return onSkillExecutedScoped(primaryTargetContext)
    }

    open fun onSkillExecutedScoped(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        return emptyList()
    }

    final override fun onMonsterStatusEffectGained(statusEffectState: StatusEffectState) {
        statusEffectState.preventsMovement = false
        statusEffectState.secondaryCounter = getMatch().getCurrentTurnCount()
    }

    final override fun onDamaged(context: ActorDamagedContext): List<Event> {
        val skill = context.skill ?: return emptyList()
        applyTpDamage(skill, context.actionContext)
        maybeKnockback(skill, context.actionContext)
        return onBehaviorDamaged(context)
    }

    fun applyTpDamage(skill: SkillId, context: AttackContext?) {
        val config = TacticsSkills[skill]
        val tpDamage = config.tpDamage ?: return

        actorState.consumeTp((tpDamage * getTpDamageMultiplier()).roundToInt())
        tpDamageResistanceStep += 1

        AttackContext.compose(context) {
            val scale = (tpDamage/150f).coerceIn(0.75f, 1.25f)
            val message = "${skill.displayName()} TP -${tpDamage}"
            DamageTextManager.append(actorState = actorState, message = message, color = ByteColor(0x80, 0x70, 0x60, 0x80), scale = scale)
        }
    }

    open fun onBehaviorDamaged(context: ActorDamagedContext): List<Event> {
        return emptyList()
    }

    final override fun onDefeated(context: ActorDefeatedContext): List<Event> {
        val match = getMatch()
        match.onUnitDefeated(context)

        endCharging()

        maybeKnockback(context.event.actionContext?.skill, context.event.actionContext)
        return emptyList()
    }

    private fun maybeKnockback(skill: SkillId?, context: AttackContext?) {
        val config = TacticsSkills.getNullable(skill) ?: return

        if (CombatBonusAggregator[actorState].knockBackResistance > 0) { return }
        if (!config.knockback && !config.drawIn) { return }

        val currentTurn = getMatch().getCurrentTurn() ?: return
        currentTurn.knockback(actorState, config.drawIn, context)
    }

    final override fun getActorCollisionType(): ActorCollisionType {
        return ActorCollisionType.None
    }

    fun submitWait(tpCost: Int) {
        val match = getMatch().getCurrentTurn() ?: throw IllegalStateException("No such turn?")
        match.actionBuilder.actorId = actorState.id
        match.actionBuilder.destination = actorState.getCursorPosition()
        match.submitWait(tpCost = tpCost, delay = 3.seconds)
    }

    open fun getSelectableSkills(): List<SkillId> {
        return getSkills()
    }

    open fun submitTurnPlan(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn) {
        val actionBuilder = turn.actionBuilder
        actionBuilder.actorId = actorState.id

        val position = actorState.getCursorPosition()
        if (position is EnemyPrepPosition) { return submitMoveForward(match, turn, position) }
        if (position !is PanelPosition) { throw IllegalStateException("Where is ${actorState.name}? $position") }

        val movementPositions = match.getMovementPositions(actorState)

        // Move the actor's 'logical space', or else skill estimation is broken
        val realPosition = Vector3f(actorState.position)
        val realFacing = actorState.rotation

        var currentBest: TacticsSkillChoice? = null
        var bestDestination: PanelPosition? = null

        val skills = getSelectableSkills().filter { !hasUsedAllSkillCharges(it) && GameEngine.canBeginSkill(actorState.id, it) }

        for (movementPosition in movementPositions) {
            // Temporarily update the state so that skill calculation works
            actorState.position.copyFrom(movementPosition.toWorldSpace())
            actionBuilder.destination = movementPosition

            // Estimate the best skill to use from this position
            val skillsWithTargets = skills.associateWith { match.getTargets(actorState, it) }.filter { it.value.isNotEmpty() }
            val bestSkillAndTarget = selectSkill(match, skillsWithTargets, position, movementPosition) ?: continue

            if (bestSkillAndTarget.score <= 0f) { continue }
            if (currentBest != null && currentBest.score >= bestSkillAndTarget.score) { continue }

            currentBest = bestSkillAndTarget
            bestDestination = movementPosition
        }

        // Fix the temporary adjustments
        actorState.position.copyFrom(realPosition)
        actorState.rotation = realFacing

        // If no applicable skills or all skills were bad, wait
        if (currentBest == null) {
            actionBuilder.destination = getFallbackMovementPosition(match, position)
            return turn.submitWait(delay = 1.seconds)
        }

        // Submit the skill
        actionBuilder.destination = bestDestination
        actionBuilder.skill = SkillChoice(currentBest.skill)
        actionBuilder.targetId = match.getUnit(currentBest.position)?.id
        return turn.submitAction()
    }

    private fun getFallbackMovementPosition(match: TacticsMatchInstance, position: PanelPosition): PanelPosition {
        val movementPositions = match.getMovementPositions(actorState)

        val playerUnits = match.getPlayerUnits()

        val desiredPositions = playerUnits.sortedBy { it.getHp() }
            .map { it.getCursorPosition() }
            .filterIsInstance<PanelPosition>()

        for (desiredPosition in desiredPositions) {
            if (desiredPosition.distance(position) == 1) { return position }

            val minPosition = movementPositions.minBy { it.distance(desiredPosition) }
            if (minPosition == position) { continue }

            return minPosition
        }

        return movementPositions.random()
    }

    fun onMatchStart(match: TacticsMatchInstance) {
        val playerUnits = match.getPlayerUnits()
        val allPlayerSkills = playerUnits.mapNotNull { it.behaviorController as? TacticsBehaviorController }
            .flatMap { it.getSkills().toSet() }
            .filterIsInstance<SpellSkillId>()

        maybeAutoProtect(match, allPlayerSkills)
        maybeAutoShell(match, allPlayerSkills)
    }

    fun onTurnStart() {
        tpDamageResistanceStep = 0
        actorState.resetRecastStates()

        recomputeSkills()
        preloadSkillEffects()

        onTurnStart(getMatch())
    }

    fun getSkillUsedCount(skill: SkillId): Int {
        return skillCounter[skill] ?: 0
    }

    fun hasUsedSkill(skill: SkillId): Boolean {
        return skillCounter.containsKey(skill)
    }

    fun hasUsedAllSkillCharges(skill: SkillId): Boolean {
        val charges = actorState.getUnit().getSkillChargeCount(skill) ?: return false
        return getSkillUsedCount(skill) >= charges
    }

    open fun onTurnStart(match: TacticsMatchInstance) { }

    fun onInstinctsChanged() {
        recomputeSkills()
    }

    fun getMatch(): TacticsMatchInstance {
        return GameV0.getCurrentTacticsZoneInstance()?.getMatch()
            ?: throw IllegalStateException("${actorState.name} is Tactics, but there is no match?")
    }

    private fun applyBlur() {
        val display = ActorManager[actorState.id] ?: return
        val bossConfig = actorState.getUnitConfig() ?: return
        display.actorModel?.customModelSettings?.blurConfig = bossConfig.blurConfig
    }

    fun recomputeSkills() {
        memoizedSkills.clear()

        val bossSkills = actorState.getUnitConfig()?.skillOverride
        if (!bossSkills.isNullOrEmpty()) {
            memoizedSkills += bossSkills
            return
        }

        memoizedSkills += actorState.getUnit().computeSkills()
    }

    fun getTpDamageMultiplier(): Float {
        return when (tpDamageResistanceStep) {
            0 -> 1f
            1 -> 0.5f
            2 -> 0.25f
            else -> 0f
        }
    }

    private fun selectSkill(
        match: TacticsMatchInstance,
        skillsWithTargets: Map<SkillId, List<PanelPosition>>,
        originalPosition: PanelPosition,
        destinationPosition: PanelPosition,
    ): TacticsSkillChoice? {
        var bestSkillTarget: Pair<SkillId, PanelPosition>? = null
        var bestScore = 0f

        for ((skill, targets) in skillsWithTargets) {
            for (target in targets) {
                val targetState = match.getUnit(target) ?: continue

                val value = estimateSkillValue(skill, targetState, originalPosition, destinationPosition) ?: continue
                if (value <= bestScore) { continue }

                bestScore = value
                bestSkillTarget = skill to target
            }
        }

        if (bestSkillTarget == null) { return null }
        return TacticsSkillChoice(bestSkillTarget.first, bestSkillTarget.second, bestScore)
    }

    private fun estimateSkillValue(skill: SkillId, target: ActorState, originalPosition: PanelPosition, destinationPosition: PanelPosition): Float? {
        val info = TacticsSkills[skill]

        if (info.aoeType == AoeType.Cone) {
            if (isRotationLocked() && !actorState.isFacingTowards(target)) { return null }
            if (!isRotationLocked()) { actorState.faceToward(target) }
        }

        val targets = SkillTargetEvaluator.evaluateTargets(skill, actorState, target)

        val result = SkillApplierRunner.applySkill(skill, info.skillApplier, actorState, targets)
        if (result !is ApplierSuccessResult) { return null }

        val query = TacticsSkillQuery(skill, target, originalPosition, destinationPosition, result)
        return estimateSkillValue(query)
    }

    open fun estimateSkillValue(query: TacticsSkillQuery): Float {
        var score = 0f
        for (event in query.result.events) {
            score += when (event) {
                is ActorAttackedEvent -> scoreActorAttackedEvent(event)
                is ActorHealedEvent -> scoreActorHealedEvent(event)
                is ActorGainStatusEvent -> scoreActorGainStatusEvent(event)
                else -> 0f
            }
        }

        return (score * Random.nextDouble(0.8, 1.2)).toFloat()
    }

    open fun behaviorEnmityMultiplier(): Float {
        return 1f
    }

    private fun scoreActorAttackedEvent(event: ActorAttackedEvent): Float {
        val attackedTarget = ActorStateManager[event.targetId] ?: return 0f
        if (event.damageType == AttackDamageType.StatusOnly) { return 1f }

        if (event.attackEffects.damageResource != ActorResourceType.HP) { return 0f }

        val damageSum = event.damageAmount.sum()
        val multiplier = if (ActionTargetFilter.areEnemies(actorState, attackedTarget)) { 5f } else { -5f }
        val fatalMultiplier = if (damageSum > attackedTarget.getHp()) { 10f } else { 1f }

        val enmity = CombatBonusAggregator[attackedTarget].enmity * behaviorEnmityMultiplier()
        val enmityMultiplier = enmity.roundToInt().toMultiplier(min = 0.1f)

        return damageSum * multiplier * fatalMultiplier * enmityMultiplier
    }

    private fun scoreActorHealedEvent(event: ActorHealedEvent): Float {
        if (event.healType != ActorResourceType.HP) { return 0f }
        val eventTarget = ActorStateManager[event.targetId] ?: return 0f

        val multiplier = if (ActionTargetFilter.areEnemies(actorState, eventTarget)) { -100f } else { 1f }
        val actualCure = min(event.amount, eventTarget.getMaxHp() - eventTarget.getHp())

        return actualCure * multiplier
    }

    private fun scoreActorGainStatusEvent(event: ActorGainStatusEvent): Float {
        val target = ActorStateManager[event.targetId] ?: return 0f
        if (target.hasStatusEffect(event.statusState.statusEffect)) { return 0f }
        return 1f
    }

    private fun submitMoveForward(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn, position: EnemyPrepPosition) {
        val destination = if (position.y == 0) {
            match.nearestUnclaimedPosition(PanelPosition(x = 2, y = 4))
        } else {
            EnemyPrepPosition(x = 0, y = position.y - 1)
        }

        turn.actionBuilder.actorId = actorState.id
        val moveForwardCost = getMoveForwardCost(destination)

        if (destination == null || match.getUnit(destination) != null) {
            turn.actionBuilder.destination = actorState.getCursorPosition()
            turn.submitWait(delay = 1.seconds, tpCost = moveForwardCost)
        } else {
            turn.actionBuilder.destination = destination
            turn.submitWait(delay = 1.seconds, tpCost = moveForwardCost)
        }
    }

    open fun getMoveForwardCost(destination: CursorPosition?): Int {
        return 200
    }

    fun displayCharging(context: AttackContext? = null) {
        AttackContext.compose(context) {
            if (charging == null) {
                val routine = ActorManager[actorState.id]?.loopRoutine(DatId("nerm"))
                charging = ChargingState(routine)
            }
        }
    }

    fun isDisplayCharging(): Boolean {
        return charging != null
    }

    fun endCharging() {
        charging?.routine?.stop()
        charging = null
    }

    private fun maybeAutoProtect(match: TacticsMatchInstance, allPlayerSpells: List<SpellSkillId>) {
        val protectIds = protects.values.toSet()
        val highestProtect = allPlayerSpells.filter { protectIds.contains(it) }.maxByOrNull { it.id } ?: return
        applySkillToParty(match, highestProtect)
    }

    private fun maybeAutoShell(match: TacticsMatchInstance, allPlayerSpells: List<SpellSkillId>) {
        val shellIds = shells.values.toSet()
        val highestShell = allPlayerSpells.filter { shellIds.contains(it) }.maxByOrNull { it.id } ?: return
        applySkillToParty(match, highestShell)
    }

    private fun applySkillToParty(match: TacticsMatchInstance, skillId: SkillId) {
        val applier = TacticsSkills[skillId].skillApplier

        for (playerUnit in match.getPlayerUnits()) {
            val fakeContext = SkillApplierHelper.TargetEvaluatorContext(
                skill = skillId,
                sourceState = playerUnit,
                targetState = playerUnit,
                primaryTargetState = playerUnit,
                context = AttackContext(),
                allTargetStates = listOf(playerUnit),
            )

            val events = applier.targetEvaluator.getEvents(fakeContext)
            events.forEach { it.apply() }
        }
    }

    private fun maybeGainSpontaneity(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext) {
        val potency = actorState.getUnit().getInstinctPotency(TacticsInstincts.spontaneity)
        if (!GameEngine.rollAgainst(potency)) { return }

        val skill = primaryTargetContext.skill

        if (skill is AbilitySkillId) {
            val type = skill.toAbilityInfo().type
            if (type != AbilityType.WeaponSkill) { return }
        } else if (skill is MobSkillId) {
            val againstEnemy = ActionTargetFilter.areEnemies(primaryTargetContext.targetState, primaryTargetContext.sourceState)
            if (!againstEnemy) { return }
        } else {
            return
        }

        val status = actorState.gainStatusEffect(StatusEffect.Spontaneity)
        status.counter = 1
        status.displayCounter = true
    }

    private fun maybeHandleTractor(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext, turnLock: TurnLock): Boolean {
        if (primaryTargetContext.skill != spellTractor_264) { return false }
        val match = getMatch()

        val source = primaryTargetContext.sourceState
        val sourcePosition = source.getCursorPosition() as? PanelPosition ?: return false

        val target = primaryTargetContext.targetState
        val targetPosition = target.getCursorPosition() as? PanelPosition ?: return false

        val destination = match.neighborsOf(sourcePosition, range = 1)
            .filter { match.getUnit(it) == null }
            .minByOrNull { it.distance(targetPosition) }
            ?: match.nearestUnclaimedPosition(sourcePosition)

        if (destination == null) {
            ChatLog("...but it failed!", ChatLogColor.Action)
            return false
        }

        target.setCursorPosition(destination)
        target.clearMovementCommand()

        warpStateMachine = WarpStateMachine(
            actorState = target,
            destination = FixedWarpDestination(destination.toWorldSpace()),
            invisibleTime = 0.5.seconds,
            warpContext = turnLock,
        )

        return true
    }

    private fun updateWarpState(elapsedFrames: Float) {
        val current = warpStateMachine ?: return

        current.update(elapsedFrames)
        if (!current.isComplete()) { return }

        current.warpContext.locked = false
        warpStateMachine = null
    }

    private fun preloadSkillEffects() {
        for (skill in getSkills()) {
            EffectDisplayer.preloadSkillResource(skill)
            val alternateSkill = TacticsSkills[skill].crossJob?.link ?: continue
            EffectDisplayer.preloadSkillResource(alternateSkill)
        }
    }

}